import React, { useState, useEffect } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";

import { useQuery, useMutation, gql } from "@apollo/client";
import {
  Container,
  Heading,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Button,
  useToast,
  Text,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Spinner,
  Box,
  HStack,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Divider,
} from "@chakra-ui/react";

import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";

import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { LoginModal } from "../authentication";
import { getColor, getComponent, brandConfig } from "../../brandConfig";

const GET_SESSION_TYPES = gql`
  query GetSessionTypes {
    sessionTypes {
      id
      name
      duration
      format
      price
    }
  }
`;

const CREATE_SESSION = gql`
  mutation CreateSession($input: SessionInput!) {
    createSession(input: $input) {
      id
      name
      startTime
      endTime
      maxParticipants
      meetingLink
      sendReminders
    }
  }
`;

// Add these interfaces for better type safety
interface SessionType {
  id: string;
  name: string;
  duration: number;
  format: "INDIVIDUAL" | "GROUP";
  maxParticipants: number;
  price: number;
}

interface SessionFormData {
  name: string;
  description: string;
  startTime: string;
  typeId: string;
  maxParticipants: number;
  meetingLink: string;
  sendReminders: boolean;
}

// First, let's define the interface to match the input type
interface SessionInput {
  name: string;
  description?: string;
  startTime: Date;
  endTime: Date;
  typeId: string;
  maxParticipants?: number;
  meetingLink?: string;
  availabilityId?: string;
  sendReminders?: boolean;
}

// Add auth check
export const ScheduleSession = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Brand styling variables
  const bgMain = getColor("background.main");
  const cardGradientBg = getColor("background.cardGradient");
  const cardBorder = getColor("border.darkCard");
  const textPrimary = getColor("text.primaryDark");
  const textSecondary = getColor("text.secondaryDark");
  const textMuted = getColor("text.mutedDark");
  const primaryColor = getColor("primary");
  const primaryHover = getColor("primaryHover");

  // Add formData state
  const [formData, setFormData] = useState<SessionFormData>({
    name: "",
    description: "",
    startTime: "",
    typeId: "",
    maxParticipants: 1,
    meetingLink: "",
    sendReminders: true,
  });

  // Add effect to handle auth state changes
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      setIsLoginModalOpen(true);
    } else {
      setIsLoginModalOpen(false);
    }
  }, [isAuthenticated, loading]);

  // Add loading and error states
  const { data: sessionTypesData, loading: loadingTypes, error: typesError } = useQuery(GET_SESSION_TYPES);
  const [createSession, { loading: creating }] = useMutation(CREATE_SESSION, {
    onCompleted: () => {
      toast({
        title: "Session created successfully",
        status: "success",
        duration: 3000,
      });
      navigate("/sessions/client-booking-calendar"); // Redirect to calendar view
    },
  });

  // Show loading state while checking auth
  if (loading) {
    return (
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8} flex="1">
          <Spinner color={primaryColor} />
        </Container>
        <Box position="sticky" bottom={0} zIndex={10}>
          <FooterWithFourColumns />
        </Box>
      </Box>
    );
  }

  // Check for both authentication and practitioner permission
  if (!isAuthenticated || !user?.permissions?.includes("PRACTITIONER")) {
    return (
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8} flex="1">
          <Text color={textPrimary} fontFamily={brandConfig.fonts.body}>This page is only accessible to practitioners.</Text>
        </Container>
        <LoginModal
          isOpen={isLoginModalOpen}
          onClose={() => setIsLoginModalOpen(false)}
          onLoginSuccess={() => {
            setIsLoginModalOpen(false);
            if (user?.permissions?.includes("PRACTITIONER")) {
              window.location.reload();
            } else {
              navigate("/");
              toast({
                title: "Access Denied",
                description: "This area is only for practitioners",
                status: "error",
                duration: 5000,
              });
            }
          }}
        />
        <Box position="sticky" bottom={0} zIndex={10}>
          <FooterWithFourColumns />
        </Box>
      </Box>
    );
  }

  // Add error state
  if (typesError) {
    return (
      <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
        <NavbarWithCallToAction />
        <Container maxW="container.xl" py={8} flex="1">
          <Text color={getColor("status.error")} fontFamily={brandConfig.fonts.body}>Error loading session types: {typesError.message}</Text>
        </Container>
        <Box position="sticky" bottom={0} zIndex={10}>
          <FooterWithFourColumns />
        </Box>
      </Box>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const selectedType = sessionTypesData?.sessionTypes.find(
        (type: SessionType) => type.id === formData.typeId
      );

      if (!selectedType) {
        throw new Error("Please select a session type");
      }

      // Calculate start and end times
      const startTime = new Date(formData.startTime);
      const endTime = new Date(startTime.getTime() + selectedType.duration * 60000);

      // Construct input object matching SessionInput type
      const input: SessionInput = {
        name: formData.name,
        typeId: selectedType.id,
        startTime,
        endTime,
      };

      // Add optional fields only if they have values
      if (formData.description) {
        input.description = formData.description;
      }

      if (formData.maxParticipants) {
        input.maxParticipants = parseInt(formData.maxParticipants.toString());
      }

      if (formData.meetingLink) {
        input.meetingLink = formData.meetingLink;
      }

      if (formData.sendReminders !== undefined) {
        input.sendReminders = formData.sendReminders;
      }

      console.log("Submitting session with data:", { input });

      await createSession({
        variables: { input }
      });

      toast({
        title: "Session created successfully",
        status: "success",
        duration: 3000,
      });

      navigate("/sessions/client-booking-calendar");
    } catch (error) {
      console.error("Error creating session:", error);
      toast({
        title: "Error creating session",
        description: error instanceof Error ? error.message : "An error occurred",
        status: "error",
        duration: 5000,
      });
    }
  };

  return (
    <Box bg={bgMain} minH="100vh" display="flex" flexDirection="column">
      <NavbarWithCallToAction />

      <Container maxW="container.lg" py={8} flex="1">
        <Flex direction="column" align="center" justify="center" minH="calc(100vh - 200px)">
          <Card
            w="full"
            maxW="600px"
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            border="1px"
            borderColor={cardBorder}
            borderRadius="lg"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 12px 40px 0 rgba(0, 0, 0, 0.5)",
              transition: "all 0.2s"
            }}
          >
            <CardHeader
              bg={primaryColor}
              color="white"
              roundedTop="md"
            >
              <Heading
                size="lg"
                textAlign="center"
                fontFamily={brandConfig.fonts.heading}
              >
                Schedule Session
              </Heading>
            </CardHeader>

            <CardBody p={8}>
              <form onSubmit={handleSubmit}>
                <VStack spacing={6} align="stretch">

                  <FormControl isRequired>
                    <FormLabel
                      fontWeight="semibold"
                      color={textSecondary}
                      fontFamily={brandConfig.fonts.body}
                    >
                      Session Type
                    </FormLabel>
                    <Select
                      placeholder="Choose session type..."
                      value={formData.typeId}
                      onChange={(e) => {
                        const selectedType = sessionTypesData?.sessionTypes.find(
                          (type: any) => type.id === e.target.value
                        );
                        setFormData({
                          ...formData,
                          typeId: e.target.value,
                          maxParticipants: selectedType?.format === "GROUP" ?
                            (selectedType?.maxParticipants || 10) : 1
                        });
                      }}
                      bg="rgba(255, 255, 255, 0.05)"
                      borderColor={cardBorder}
                      color={textPrimary}
                      _hover={{ borderColor: textSecondary }}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`,
                      }}
                      fontFamily={brandConfig.fonts.body}
                    >
                      {sessionTypesData?.sessionTypes?.map((sessionType: any) => (
                        <option key={sessionType.id} value={sessionType.id} style={{ background: '#1a202c', color: '#e2e8f0' }}>
                          {sessionType.name} - {sessionType.duration} mins (${sessionType.price})
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      fontWeight="semibold"
                      color={textSecondary}
                      fontFamily={brandConfig.fonts.body}
                    >
                      Session Title
                    </FormLabel>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., Morning Meditation Session"
                      bg="rgba(255, 255, 255, 0.05)"
                      borderColor={cardBorder}
                      color={textPrimary}
                      _placeholder={{ color: textMuted }}
                      _hover={{ borderColor: textSecondary }}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      fontFamily={brandConfig.fonts.body}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontWeight="semibold"
                      color={textSecondary}
                      fontFamily={brandConfig.fonts.body}
                    >
                      Description
                    </FormLabel>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe what participants can expect"
                      bg="rgba(255, 255, 255, 0.05)"
                      borderColor={cardBorder}
                      color={textPrimary}
                      _placeholder={{ color: textMuted }}
                      _hover={{ borderColor: textSecondary }}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      fontFamily={brandConfig.fonts.body}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel
                      fontWeight="semibold"
                      color={textSecondary}
                      fontFamily={brandConfig.fonts.body}
                    >
                      Start Time
                    </FormLabel>
                    <Input
                      type="datetime-local"
                      value={formData.startTime}
                      onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                      bg="rgba(255, 255, 255, 0.05)"
                      borderColor={cardBorder}
                      color={textPrimary}
                      _hover={{ borderColor: textSecondary }}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      fontFamily={brandConfig.fonts.body}
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontWeight="semibold"
                      color={textSecondary}
                      fontFamily={brandConfig.fonts.body}
                    >
                      Maximum Participants
                    </FormLabel>
                    <NumberInput
                      value={formData.maxParticipants}
                      onChange={(value) => setFormData({ ...formData, maxParticipants: parseInt(value) })}
                      min={1}
                    >
                      <NumberInputField
                        bg="rgba(255, 255, 255, 0.05)"
                        borderColor={cardBorder}
                        color={textPrimary}
                        _hover={{ borderColor: textSecondary }}
                        _focus={{
                          borderColor: primaryColor,
                          boxShadow: `0 0 0 1px ${primaryColor}`
                        }}
                        fontFamily={brandConfig.fonts.body}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper color={textSecondary} borderColor={cardBorder} _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} />
                        <NumberDecrementStepper color={textSecondary} borderColor={cardBorder} _hover={{ bg: "rgba(255, 255, 255, 0.1)" }} />
                      </NumberInputStepper>
                    </NumberInput>
                  </FormControl>

                  <FormControl>
                    <FormLabel
                      fontWeight="semibold"
                      color={textSecondary}
                      fontFamily={brandConfig.fonts.body}
                    >
                      Meeting Link (for virtual sessions)
                    </FormLabel>
                    <Input
                      value={formData.meetingLink}
                      onChange={(e) => setFormData({ ...formData, meetingLink: e.target.value })}
                      placeholder="https://zoom.us/..."
                      bg="rgba(255, 255, 255, 0.05)"
                      borderColor={cardBorder}
                      color={textPrimary}
                      _placeholder={{ color: textMuted }}
                      _hover={{ borderColor: textSecondary }}
                      _focus={{
                        borderColor: primaryColor,
                        boxShadow: `0 0 0 1px ${primaryColor}`
                      }}
                      fontFamily={brandConfig.fonts.body}
                    />
                  </FormControl>

                  <FormControl display="flex" alignItems="center">
                    <FormLabel mb="0"
                      fontWeight="semibold"
                      color={textSecondary}
                      fontFamily={brandConfig.fonts.body}
                    >
                      Send Reminders
                    </FormLabel>
                    <Switch
                      isChecked={formData.sendReminders}
                      onChange={(e) => setFormData({ ...formData, sendReminders: e.target.checked })}
                      colorScheme="blue"
                    />
                  </FormControl>

                  <Divider my={4} borderColor={cardBorder} />

                  <Button
                    type="submit"
                    isLoading={creating}
                    loadingText="Scheduling..."
                    bg={primaryColor}
                    color="white"
                    size="lg"
                    w="full"
                    _hover={{
                      bg: primaryHover,
                      transform: "translateY(-2px)"
                    }}
                    _active={{ transform: "translateY(0)" }}
                    transition="all 0.2s"
                    fontFamily={brandConfig.fonts.body}
                    fontWeight="semibold"
                  >
                    Schedule Session
                  </Button>

                  <Text
                    fontSize="sm"
                    color={textMuted}
                    textAlign="center"
                    fontFamily={brandConfig.fonts.body}
                  >
                    The client will receive confirmation via email and SMS
                  </Text>
                </VStack>
              </form>
            </CardBody>
          </Card>
        </Flex>
      </Container>

      <Box position="sticky" bottom={0} zIndex={10}>
        <FooterWithFourColumns />
      </Box>
    </Box>
  );
}; 